/**
 * Mein Konto — profile and address editing. BS custom, 2026-07-30.
 *
 * Talks to the BS portal through the Shopify app proxy (/apps/account/*), which
 * is what makes writing to a customer record safe from the storefront: Shopify
 * signs each request and supplies the customer id, so nothing here has to be
 * trusted.
 *
 * The initial state is rendered by Liquid, not fetched — including the USt-IdNr.
 * metafield, which Liquid can read because a metafield definition exists for
 * konto.ust_id. So the page is correct on first paint with no spinner. After a successful write we simply reload;
 * Liquid then re-renders from Shopify and there is no second copy of the state
 * to keep in step.
 */
(() => {
  const PROFILE = '/apps/account/profile';
  const ADDRESSES = '/apps/account/addresses';

  class KontoAccount extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', this.onClick.bind(this));
      this.addEventListener('submit', this.onSubmit.bind(this));
    }

    /* ------------------------------------------------------------ helpers */

    panelFor(el) {
      return el.closest('[data-konto-panel]');
    }

    setBusy(panel, busy) {
      panel.querySelectorAll('button, input, select').forEach((c) => {
        c.disabled = busy;
      });
    }

    showError(panel, message) {
      const box = panel.querySelector('[data-konto-error]');
      if (!box) return;
      box.textContent = message || '';
      box.hidden = !message;
    }

    async send(url, payload, panel) {
      this.setBusy(panel, true);
      this.showError(panel, '');
      try {
        const res = await fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        const data = await res.json().catch(() => null);
        if (res.ok && data?.ok) {
          // Reload rather than patch the DOM: Liquid owns this state.
          window.location.reload();
          return true;
        }
        this.showError(panel, data?.error || 'Speichern fehlgeschlagen. Bitte später erneut versuchen.');
      } catch {
        this.showError(panel, 'Keine Verbindung. Bitte prüfen Sie Ihre Internetverbindung.');
      }
      this.setBusy(panel, false);
      return false;
    }

    /* ------------------------------------------------------------- events */

    onClick(evt) {
      const toggle = evt.target.closest('[data-konto-toggle]');
      if (toggle) {
        evt.preventDefault();
        const target = this.querySelector(`#${toggle.dataset.kontoToggle}`);
        if (target) {
          target.hidden = !target.hidden;
          toggle.setAttribute('aria-expanded', String(!target.hidden));
          if (!target.hidden) target.querySelector('input, select')?.focus();
        }
        return;
      }

      const del = evt.target.closest('[data-konto-delete]');
      if (del) {
        evt.preventDefault();
        // Native confirm() is blocked in some embedded contexts and is ugly, so
        // the button asks for a second click instead.
        if (del.dataset.armed !== '1') {
          del.dataset.armed = '1';
          del.dataset.label = del.textContent;
          del.textContent = 'Wirklich löschen?';
          setTimeout(() => {
            if (del.dataset.armed === '1') {
              del.dataset.armed = '0';
              del.textContent = del.dataset.label;
            }
          }, 5000);
          return;
        }
        this.send(ADDRESSES, { action: 'delete', id: del.dataset.kontoDelete }, this.panelFor(del));
        return;
      }

      const def = evt.target.closest('[data-konto-default]');
      if (def) {
        evt.preventDefault();
        this.send(ADDRESSES, { action: 'default', id: def.dataset.kontoDefault }, this.panelFor(def));
      }
    }

    onSubmit(evt) {
      const form = evt.target.closest('form[data-konto-form]');
      if (!form) return;
      evt.preventDefault();
      const panel = this.panelFor(form);
      const kind = form.dataset.kontoForm;
      const fd = new FormData(form);

      if (kind === 'profile') {
        this.send(
          PROFILE,
          {
            firstName: fd.get('first_name') || '',
            lastName: fd.get('last_name') || '',
            phone: fd.get('phone') || '',
            vatId: fd.get('vat_id') || '',
          },
          panel
        );
        return;
      }

      const address = {};
      ['first_name', 'last_name', 'company', 'address1', 'address2', 'zip', 'city', 'country', 'phone']
        .forEach((k) => { address[k] = fd.get(k) || ''; });

      this.send(
        ADDRESSES,
        { action: form.dataset.kontoId ? 'update' : 'create', id: form.dataset.kontoId || undefined, address },
        panel
      );
    }
  }

  if (!customElements.get('konto-account')) customElements.define('konto-account', KontoAccount);
})();
